/**
 * Winclap Studio — Programas (spec 12: pestañas Programas y Dashboard). Tanda 1: la tarjeta
 * del Product Tour con el avance de la persona y, para admins de Learning, el historial de
 * versiones del contenido (spec 7.4). El Dashboard de líderes y People llega en la tanda 2.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, GraduationCap, History, Play, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { toastError } from '@/lib/toast-error';
import { STUDIO_CATEGORY_LABELS_ES } from '@/../../shared/studio-tour';

export function StudioPage() {
  const { t } = useTranslation();
  const tour = trpc.studioTour.getMyTour.useQuery(undefined, { staleTime: 30_000 });
  const canEdit = !!tour.data?.me.canEdit;
  const history = trpc.studioTour.getContentHistory.useQuery(undefined, { enabled: canEdit });
  const utils = trpc.useUtils();
  const revert = trpc.studioTour.revertContent.useMutation({
    onSuccess: (r) => { toast.success(t('studio.admin.restored', 'Contenido restaurado como versión {{v}}', { v: r.version })); void utils.studioTour.invalidate(); },
    onError: (e) => toastError(e, 'studio.admin.restore_error'),
  });

  if (tour.isLoading) {
    return <div className="flex h-64 items-center justify-center"><WinclapLoader size={48} className="text-foreground/25" /></div>;
  }
  if (tour.error || !tour.data) {
    return <p className="p-6 text-sm text-destructive">{tour.error?.message ?? t('studio.tour.load_error', 'No se pudo cargar el Product Tour')}</p>;
  }
  const { summary, me, content } = tour.data;
  const minutes = content.modules.reduce((a, m) => a + m.steps.reduce((b, s) => b + (s.min ?? 0), 0), 0);
  const statusLabel = summary.certified
    ? t('studio.status.certified', 'Certificado')
    : summary.stepsDone > 0
      ? t('studio.status.in_progress', 'En progreso')
      : t('studio.status.not_started', 'Sin iniciar');
  const cta = summary.certified
    ? t('studio.tour.review', 'Volver a ver')
    : summary.stepsDone > 0
      ? t('studio.tour.continue', 'Continuar')
      : t('studio.tour.start', 'Empezar');

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><GraduationCap className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-semibold">{t('studio.title', 'Winclap Studio')}</h1>
          <p className="text-sm text-muted-foreground">{t('studio.subtitle', 'Programas del Studio: tu recorrido por Agentic, Brkaway y Claude.')}</p>
        </div>
      </div>

      <h2 className="text-lg font-medium">{t('studio.programs', 'Programas')}</h2>
      <Card className="max-w-2xl">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>{t('studio.tour.card_title', 'Onboarding: Product Tour')}</CardTitle>
              <CardDescription>{t('studio.tour.card_desc', 'Seis módulos: bienvenida y contexto, Agentic, Brkaway, consideraciones del proceso, certificación y tu primera cuenta.')}</CardDescription>
            </div>
            <span className="shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-medium">{statusLabel}</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
              <span>{t('studio.tour.progress', '{{done}} de {{total}} pasos', { done: summary.stepsDone, total: summary.stepsTotal })}</span>
              <span>{summary.pct} %</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${summary.pct}%` }} /></div>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{t('studio.tour.duration', '~{{min}} min en total', { min: minutes })}</span>
            {summary.elapsedMin > 0 && <span>{t('studio.tour.elapsed', 'Llevás {{min}} min', { min: summary.elapsedMin })}</span>}
            {summary.currentModuleTitle && !summary.certified && <span>{t('studio.tour.current', 'Estás en {{module}}', { module: summary.currentModuleTitle })}</span>}
            <span>{t('studio.tour.category', 'Categoría: {{cat}}', { cat: STUDIO_CATEGORY_LABELS_ES[me.category] })}</span>
          </div>
          <Button asChild>
            <Link to="/studio/product-tour"><Play className="mr-1.5 h-4 w-4" />{cta}</Link>
          </Button>
        </CardContent>
      </Card>

      {canEdit && (
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4" />{t('studio.admin.versions', 'Versiones del contenido')}</CardTitle>
            <CardDescription>{t('studio.admin.versions_desc', 'Cada edición de textos o notas desde el recorrido queda como una versión nueva, con fecha y autor. Restaurar publica esa versión como una nueva.')}</CardDescription>
          </CardHeader>
          <CardContent>
            {history.isLoading ? (
              <WinclapLoader size={24} className="text-foreground/25" />
            ) : !history.data?.length ? (
              <p className="text-sm text-muted-foreground">{t('studio.admin.no_versions', 'Todavía no hay ediciones: se muestra el contenido original del prototipo (v{{v}}).', { v: content.version })}</p>
            ) : (
              <ul className="divide-y text-sm">
                {history.data.map((v) => (
                  <li key={v.version} className="flex items-center justify-between gap-3 py-2">
                    <div>
                      <span className="font-medium">v{v.version}</span>
                      <span className="ml-2 text-muted-foreground">{new Date(v.updatedAt).toLocaleString()} · {v.updatedBy}</span>
                      {v.note && <div className="text-xs text-muted-foreground">{v.note}</div>}
                    </div>
                    {v.version !== content.version && (
                      <Button size="sm" variant="outline" disabled={revert.isPending} onClick={() => revert.mutate({ version: v.version })}>
                        <RotateCcw className="mr-1 h-3.5 w-3.5" />{t('studio.admin.restore', 'Restaurar')}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
